/** Moldura das telas da equipe: menu lateral (desktop) / menu sanduíche (celular) + conteúdo. */
import { useEffect, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router";
import { api } from "../lib/api";
import { useSession } from "../lib/session";
import { Button } from "./Button";

const OWNER_EMAIL = "laralivia925@gmail.com";

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

const BASE_NAV: NavItem[] = [
  { to: "/gestao", label: "Gestão", icon: "📋" },
  { to: "/mapas", label: "Mapas Mentais", icon: "🧠" },
  { to: "/convites", label: "Convites", icon: "✉️" },
  { to: "/dashboard", label: "Dashboard", icon: "📊" },
  { to: "/arquitetura-onboarding", label: "Onboarding", icon: "🚀" },
];

export function AdminShell({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, setSession } = useSession();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // O Histórico só aparece para a conta da Lara / superadmin
  const isOwner = (session?.email ?? "").toLowerCase() === OWNER_EMAIL || session?.role === "superadmin";
  const NAV: NavItem[] = isOwner
    ? [...BASE_NAV, { to: "/historico", label: "Histórico", icon: "📜" }]
    : BASE_NAV;

  // Fecha o menu móvel ao mudar de rota ou ao pressionar Escape
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setMobileMenuOpen(false);
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  async function logout() {
    await api.post("/auth/logout").catch(() => undefined);
    setSession(null);
    navigate("/entrar", { replace: true });
  }

  const active = (to: string): boolean =>
    location.pathname === to ||
    location.pathname.startsWith(`${to}/`) ||
    (to === "/gestao" && location.pathname.startsWith("/clientes")) ||
    (to === "/convites" && (location.pathname === "/convites" || location.pathname === "/cadastro")) ||
    (to === "/mapas" && (location.pathname.startsWith("/mapas/") || location.pathname.includes("/mapas")));

  const currentItem = NAV.find((n) => active(n.to)) ?? NAV[0];

  return (
    <div className="app-shell min-h-dvh sm:flex bg-[#0f0505]">
      {/* Menu Lateral Desktop */}
      <aside className="hidden sm:flex sm:w-60 sm:shrink-0 sm:flex-col sm:border-r sm:border-border-subtle/80 sm:bg-[#160a0a]/95 sm:px-4 sm:py-6 backdrop-blur-md">
        <Link to="/gestao" className="px-2 flex items-center gap-2.5">
          <img src="/brand/logotipo-principal-creme.png" alt="Unik" className="h-6 w-auto" />
          <span className="text-[11px] uppercase tracking-widest text-gold/80 font-mono">Platform</span>
        </Link>
        <nav className="mt-8 flex flex-col gap-1.5">
          {NAV.map((n) => {
            const isAct = active(n.to);
            return (
              <Link
                key={n.to}
                to={n.to}
                className={`flex items-center gap-3 rounded-[var(--radius-control)] px-3.5 py-2.5 text-sm font-medium transition-all ${
                  isAct
                    ? "bg-gold/15 text-gold border border-gold/30 shadow-sm"
                    : "text-text-secondary hover:bg-white/5 hover:text-text-primary"
                }`}
              >
                <span className="text-base">{n.icon}</span>
                <span>{n.label}</span>
                {n.to === "/mapas" && (
                  <span className="ml-auto rounded-full bg-gold/20 px-1.5 py-0.5 text-[10px] font-mono text-gold">
                    Mind
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="mt-auto pt-6 border-t border-border-subtle/60">
          <div className="mb-3 px-2">
            <p className="text-xs text-text-tertiary truncate font-mono">{session?.email ?? "Equipe Unik"}</p>
          </div>
          <Button variant="ghost" size="sm" fullWidth onClick={logout}>
            🚪 Sair do Sistema
          </Button>
        </div>
      </aside>

      <div className="min-w-0 flex-1 flex flex-col">
        {/* Cabeçalho Mobile com Botão Sanduíche */}
        <header className="sticky top-0 z-[var(--z-sticky)] flex items-center justify-between border-b border-border-subtle/80 bg-[#160a0a]/95 px-4 py-3 backdrop-blur-md sm:hidden">
          <Link to="/gestao" className="flex items-center gap-2 shrink-0">
            <img src="/brand/logotipo-principal-creme.png" alt="Unik" className="h-5 w-auto" />
          </Link>

          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-gold/90 bg-gold/10 px-2 py-0.5 rounded-full border border-gold/20">
              {currentItem?.icon} {currentItem?.label}
            </span>

            {/* Botão Menu Sanduíche */}
            <button
              type="button"
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label="Abrir menu de navegação"
              aria-expanded={mobileMenuOpen}
              className="flex h-9 w-9 items-center justify-center rounded-[var(--radius-control-sm)] border border-border-default/80 bg-surface-elevated text-text-cream hover:bg-surface-sunken focus:outline-none"
            >
              {mobileMenuOpen ? (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>
          </div>
        </header>

        {/* Drawer do Menu Sanduíche Mobile */}
        {mobileMenuOpen && (
          <div className="fixed inset-0 z-[100] flex flex-col bg-[#140707]/98 backdrop-blur-2xl p-6 sm:hidden animate-fade-in">
            <div className="flex items-center justify-between border-b border-border-subtle/80 pb-4">
              <Link to="/gestao" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2">
                <img src="/brand/logotipo-principal-creme.png" alt="Unik" className="h-6 w-auto" />
                <span className="text-xs uppercase tracking-widest text-gold font-mono">Platform</span>
              </Link>
              <button
                type="button"
                onClick={() => setMobileMenuOpen(false)}
                className="rounded-full p-2 text-text-tertiary hover:bg-white/10 hover:text-text-primary"
              >
                ✕
              </button>
            </div>

            <nav className="mt-6 flex flex-col gap-2">
              {NAV.map((n) => {
                const isAct = active(n.to);
                return (
                  <Link
                    key={n.to}
                    to={n.to}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center justify-between rounded-xl p-3.5 text-base font-medium transition-all ${
                      isAct
                        ? "bg-gold/20 text-gold border border-gold/40 shadow-md"
                        : "text-text-secondary hover:bg-white/5 hover:text-text-cream"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{n.icon}</span>
                      <span>{n.label}</span>
                    </div>
                    {isAct && <span className="text-gold font-bold">●</span>}
                  </Link>
                );
              })}
            </nav>

            <div className="mt-auto pt-6 border-t border-border-subtle/60 flex flex-col gap-3">
              <div className="flex items-center gap-3 rounded-lg bg-surface-sunken/60 p-3">
                <div className="h-8 w-8 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center text-xs font-bold text-gold">
                  {session?.name ? session.name.charAt(0).toUpperCase() : "U"}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium text-text-cream truncate">{session?.name ?? "Equipe Unik"}</p>
                  <p className="text-[11px] text-text-tertiary truncate font-mono">{session?.email ?? ""}</p>
                </div>
              </div>
              <Button variant="ghost" size="md" fullWidth onClick={logout}>
                🚪 Sair do Sistema
              </Button>
            </div>
          </div>
        )}

        <main className="mx-auto w-full max-w-[1920px] px-4 sm:px-8 lg:px-10 py-6 flex-1">{children}</main>
      </div>
    </div>
  );
}

